/**
 * Utility functions for user-related operations
 */

import { User } from "@auth0/auth0-react";
import { STORAGE_KEYS } from "@/constants/constants";

// Type for user profile data from /exchange API
interface SessionUserProfile {
  firstName?: string;
  lastName?: string;
  username?: string;
  role?: string;
  token?: string;
}

/**
 * Gets user profile data from session storage (from /exchange API)
 * @returns User profile object or null if not found
 */
export const getSessionUserProfile = (): SessionUserProfile | null => {
  try {
    const userProfileData = sessionStorage.getItem(STORAGE_KEYS.USER_PROFILE);
    if (userProfileData) {
      return JSON.parse(userProfileData) as SessionUserProfile;
    }
  } catch (error) {
    console.warn("Failed to parse user profile from session storage:", error);
  }
  return null;
};

/**
 * Generate user initials from session data or Auth0 user object
 * Prioritizes session data, falls back to Auth0 user data
 * Returns first letter of username if available, otherwise uses name initials
 */
export const getUserInitials = (user: User | undefined): string => {
  // First try to get username from session user profile and use first letter
  const sessionProfile = getSessionUserProfile();
  if (sessionProfile?.username) {
    return sessionProfile.username[0].toUpperCase();
  }
  
  // Try to get initials from session user profile
  if (sessionProfile?.firstName && sessionProfile?.lastName) {
    return `${sessionProfile.firstName[0]}${sessionProfile.lastName[0]}`.toUpperCase();
  }
  
  if (sessionProfile?.firstName) {
    const name = sessionProfile.firstName;
    if (name.length >= 2) {
      return `${name[0]}${name[1]}`.toUpperCase();
    }
    return `${name[0]}${name[0]}`.toUpperCase();
  }

  // Fallback to Auth0 user data
  if (!user) {
    return "U";
  }

  // Try nickname first (often contains username)
  if (user.nickname) {
    return user.nickname[0].toUpperCase();
  }

  // Try to get first and last name from given_name and family_name
  const firstName = user.given_name;
  const lastName = user.family_name;
  
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  
  // Fallback: try to extract from full name
  if (user.name) {
    const nameParts = user.name.trim().split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    }
    // If only one name part, use first letter
    const singleName = nameParts[0];
    return singleName[0].toUpperCase();
  }
  
  // Try email as last resort
  if (user.email) {
    return user.email[0].toUpperCase();
  }
  
  return "U";
};

/**
 * Get user's first name for greetings
 * Prioritizes session data from /exchange API, falls back to Auth0 user data
 */
export const getUserFirstName = (user: User | undefined): string => {
  // First try to get first name from session user profile
  const sessionProfile = getSessionUserProfile();
  if (sessionProfile?.firstName) {
    return sessionProfile.firstName;
  }

  // Fallback to Auth0 user data
  if (!user) return 'there';
  
  // First try to extract first name from full name
  if (user.name) {
    const firstNameFromFullName = user.name.trim().split(' ')[0];
    if (firstNameFromFullName) {
      return firstNameFromFullName;
    }
  }
  
  // Fallback to given_name if full name is not available
  if (user.given_name) {
    return user.given_name;
  }
  
  // Final fallback
  return 'there';
};

/**
 * Get username from session data or Auth0 user object
 * Prioritizes session data from /exchange API, falls back to Auth0 user data
 */
export const getUsername = (user: User | undefined): string => {
  // First try to get username from session user profile
  const sessionProfile = getSessionUserProfile();
  if (sessionProfile?.username) {
    return sessionProfile.username;
  }

  // Fallback to Auth0 user data
  if (!user) return "User";

  // Try nickname first
  if (user.nickname) {
    return user.nickname;
  }

  // Try to extract from name
  if (user.name) {
    const nameParts = user.name.trim().split(' ');
    if (nameParts.length > 0) {
      return nameParts[0];
    }
  }

  // Try given_name
  if (user.given_name) {
    return user.given_name;
  }

  // Extract from email as last resort
  if (user.email) {
    return user.email.split('@')[0];
  }

  return "User";
};